import React from "react";
import {
    Autocomplete,
    Box,
    Button,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Delete } from "@mui/icons-material";
import { format, parse } from "date-fns";

const currencies = ["USD", "CAD"];

export default function LineItemsBox({ invoice, onUpdate, projectDocket }) {
    const handleItemsChange = (newItems) => {
        onUpdate("items", newItems);
    };

    const handleNotesChange = (newNotes) => {
        onUpdate("description", newNotes);
    };

    const handleInvoiceChange = (field, value) => {
        onUpdate(field, value);
    };

    const handleToggleSplit = (split) => {
        if (split) {
            onUpdate("itemsMerged", invoice.items);
            onUpdate("items", invoice.itemsSplitted || []);
        } else {
            onUpdate("itemsSplitted", invoice.items);
            onUpdate("items", invoice.itemsMerged || []);
        }
        onUpdate("splitByLineItems", split);
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ fontSize: "1.2rem", mb: 3 }}>
                Bill Details
            </Typography>

            <InvoiceForm invoice={invoice} setInvoice={handleInvoiceChange} />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    mt: 4,
                }}
            >
                <Typography variant="h6" sx={{ fontSize: "1.2rem" }}>
                    Line Items
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={invoice.splitByLineItems}
                            onChange={(e) =>
                                handleToggleSplit(e.target.checked)
                            }
                        />
                    }
                    label={
                        <Typography fontSize={14}>
                            Split bill by line items
                        </Typography>
                    }
                />
            </Box>

            <LineItems
                invoice={invoice}
                setItems={handleItemsChange}
                setNotes={handleNotesChange}
                projectDocket={projectDocket}
            />
        </Box>
    );
}

function InvoiceForm({ invoice, setInvoice }) {
    const handleSetInvoice = (field, value) => {
        setInvoice(field, value);
    };

    const parseRawCalendarValue = (newValue) => {
        let date = newValue;
        if (date != null) {
            try {
                date = format(newValue, "y-MM-dd");
            } catch (e) {
                date = "";
            }
        }
        return date;
    };

    return (
        <Grid container spacing={3}>
            <Grid item md={4} xs={12}>
                <InputLabel sx={{ mb: 1 }}>Vendor Name</InputLabel>
                <TextField
                    fullWidth
                    size="small"
                    onChange={(e) => setInvoice("vendor_name", e.target.value)}
                    value={invoice.vendor_name || ""}
                />
            </Grid>
            <Grid item md={4} xs={12}>
                <InputLabel sx={{ mb: 1 }}>Your Email</InputLabel>
                <TextField
                    fullWidth
                    size="small"
                    onChange={(e) => setInvoice("vendor_email", e.target.value)}
                    value={invoice.vendor_email || ""}
                />
            </Grid>
            <Grid item md={4} xs={12}>
                <InputLabel sx={{ mb: 1 }}>Currency</InputLabel>
                <Autocomplete
                    size="small"
                    value={invoice.currency || null}
                    options={currencies}
                    renderInput={(params) => <TextField {...params} />}
                    onChange={(_, newValue) =>
                        handleSetInvoice("currency", newValue)
                    }
                />
            </Grid>

            <Grid item md={4} xs={12}>
                <InputLabel sx={{ mb: 1 }}>Invoice Number</InputLabel>
                <TextField
                    fullWidth
                    size="small"
                    onChange={(e) =>
                        handleSetInvoice("invoice_number", e.target.value)
                    }
                    value={invoice.invoice_number || ""}
                />
            </Grid>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid item md={4} xs={6}>
                    <InputLabel sx={{ mb: 1 }}>Invoice Date</InputLabel>
                    <DatePicker
                        value={
                            invoice.invoice_date
                                ? parse(
                                      invoice.invoice_date,
                                      "y-MM-dd",
                                      new Date(),
                                  )
                                : null
                        }
                        inputFormat={"yyyy-MM-dd"}
                        onChange={(value) =>
                            handleSetInvoice(
                                "invoice_date",
                                parseRawCalendarValue(value),
                            )
                        }
                        onAccept={(value) =>
                            handleSetInvoice(
                                "invoice_date",
                                parseRawCalendarValue(value),
                            )
                        }
                        renderInput={(params) => (
                            <TextField size="small" fullWidth {...params} />
                        )}
                    />
                </Grid>
                <Grid item md={4} xs={6}>
                    <InputLabel sx={{ mb: 1 }}>Due Date</InputLabel>
                    <DatePicker
                        value={
                            invoice.due_date
                                ? parse(invoice.due_date, "y-MM-dd", new Date())
                                : null
                        }
                        inputFormat={"yyyy-MM-dd"}
                        onChange={(value) =>
                            handleSetInvoice(
                                "due_date",
                                parseRawCalendarValue(value),
                            )
                        }
                        onAccept={(value) =>
                            handleSetInvoice(
                                "due_date",
                                parseRawCalendarValue(value),
                            )
                        }
                        renderInput={(params) => (
                            <TextField size="small" fullWidth {...params} />
                        )}
                    />
                </Grid>
            </LocalizationProvider>
        </Grid>
    );
}

function LineItems({ invoice, setItems, setNotes, projectDocket }) {
    const { items, description } = invoice;

    const handleItemChange = (idx, field, value) => {
        const newItems = items.map((item, i) =>
            i === idx ? { ...item, [field]: value } : item,
        );
        setItems(newItems);
    };

    const handleDeleteItem = (idx) => {
        setItems(items.filter((_, i) => i !== idx));
    };

    const handleAddItem = () => {
        setItems([
            ...items,
            {
                name: "",
                docket: projectDocket || "",
                amount: 0,
                tax: 0,
                total: 0,
                description: "",
                currency: "",
            },
        ]);
    };

    const handleFocus = (event) => {
        event.target.select();
    };

    return (
        <Box>
            <Grid
                container
                spacing={1}
                sx={{
                    mb: 2,
                    fontWeight: 500,
                    flexWrap: "nowrap",
                    opacity: 0.7,
                }}
            >
                <Grid item sx={{ fontSize: 13, width: 140 }}>
                    Item
                </Grid>
                <Grid item flexGrow={1} sx={{ fontSize: 13, minWidth: 100 }}>
                    Notes
                </Grid>
                <Grid item sx={{ fontSize: 13, width: 80 }}>
                    Docket
                </Grid>
                <Grid item sx={{ fontSize: 13, width: 90, textAlign: "right" }}>
                    Subtotal
                </Grid>
                <Grid item sx={{ fontSize: 13, width: 80, textAlign: "right" }}>
                    Tax
                </Grid>
                <Grid item sx={{ fontSize: 13, width: 90, textAlign: "right" }}>
                    Total
                </Grid>
                <Grid item sx={{ width: 40 }}></Grid>
            </Grid>

            {items.map((item, idx) => (
                <Grid
                    container
                    spacing={1}
                    alignItems="flex-start"
                    key={idx}
                    sx={{ mb: 1.5, flexWrap: "nowrap" }}
                >
                    <Grid item sx={{ width: 140 }}>
                        <TextField
                            value={item.name || ""}
                            onChange={(e) =>
                                handleItemChange(idx, "name", e.target.value)
                            }
                            size="small"
                            variant="outlined"
                            sx={{ width: "100%", input: { p: "6px 8px" } }}
                            InputProps={{ sx: { fontSize: 13, p: 0 } }}
                        />
                    </Grid>
                    <Grid item flexGrow={1} sx={{ minWidth: 100 }}>
                        <TextField
                            value={item.description || ""}
                            onChange={(e) =>
                                handleItemChange(
                                    idx,
                                    "description",
                                    e.target.value,
                                )
                            }
                            size="small"
                            variant="outlined"
                            sx={{ width: "100%", input: { p: "6px 8px" } }}
                            InputProps={{ sx: { fontSize: 13, p: 0 } }}
                        />
                    </Grid>
                    <Grid item sx={{ width: 80 }}>
                        <TextField
                            value={item.docket || ""}
                            onChange={(e) =>
                                handleItemChange(idx, "docket", e.target.value)
                            }
                            size="small"
                            variant="outlined"
                            sx={{ input: { p: "6px 8px" } }}
                            InputProps={{ sx: { fontSize: 13, p: 0 } }}
                        />
                    </Grid>
                    <Grid item sx={{ width: 90 }}>
                        <TextField
                            onFocus={handleFocus}
                            value={item.amount || ""}
                            onChange={(e) =>
                                handleItemChange(idx, "amount", e.target.value)
                            }
                            size="small"
                            variant="outlined"
                            sx={{ input: { p: "6px 8px" } }}
                            InputProps={{
                                sx: {
                                    fontSize: 13,
                                    p: 0,
                                    "& input": {
                                        textAlign: "right",
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item sx={{ width: 80 }}>
                        <TextField
                            onFocus={handleFocus}
                            value={item.tax || ""}
                            onChange={(e) =>
                                handleItemChange(idx, "tax", e.target.value)
                            }
                            size="small"
                            variant="outlined"
                            sx={{ input: { p: "6px 8px" } }}
                            InputProps={{
                                sx: {
                                    fontSize: 13,
                                    p: 0,
                                    "& input": {
                                        textAlign: "right",
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item sx={{ width: 90 }}>
                        <TextField
                            disabled
                            value={
                                parseFloat(item.tax || 0) +
                                parseFloat(item.amount || 0)
                            }
                            size="small"
                            variant="outlined"
                            sx={{ input: { p: "6px 8px" } }}
                            InputProps={{
                                sx: {
                                    fontSize: 13,
                                    p: 0,
                                    "& input": {
                                        textAlign: "right",
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item sx={{ width: 40 }}>
                        <IconButton
                            onClick={() => handleDeleteItem(idx)}
                            size="small"
                            sx={{ mt: "2px" }}
                        >
                            <Delete sx={{ color: "#aaa", fontSize: 18 }} />
                        </IconButton>
                    </Grid>
                </Grid>
            ))}

            {invoice.splitByLineItems && (
                <Button
                    onClick={handleAddItem}
                    size="small"
                    sx={{
                        color: "#4caf50",
                        textTransform: "none",
                        mb: 3,
                        mt: 1,
                    }}
                >
                    + Add bill item
                </Button>
            )}

            <Box sx={{ mt: 1 }}>
                <InputLabel sx={{ mb: 1 }}>Bill Notes</InputLabel>
                <TextField
                    value={description}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    sx={{ textarea: { color: "#fff" } }}
                />
            </Box>
        </Box>
    );
}
